import { FC } from 'react';
import { Star, X } from 'lucide-react';

import { Badge } from '@new/common/ui/Badge/Badge';
import type { PlanCourse } from '@teachur/http-client';
import { cn } from '@new/lib';
import { statusBadge } from '../../lib/planView';

interface CourseRowProps {
  course: PlanCourse;
  recommended?: boolean;
  onSwitch?: (course: PlanCourse) => void;
  onRemove?: (course: PlanCourse) => void;
}

export const CourseRow: FC<CourseRowProps> = ({
  course,
  recommended = false,
  onSwitch,
  onRemove,
}) => {
  const isTransfer = course.source === 'transfer';
  const isCompleted = course.status === 'passed';
  const badge = statusBadge(course.status);
  const locked = isTransfer || isCompleted;

  const canSwitch = !!onSwitch && !locked;
  const canRemove = !!onRemove && !locked;
  const hasActions = canSwitch || canRemove;

  const iconWrapClass = isTransfer
    ? 'bg-(--secondary-B100)'
    : isCompleted
      ? 'bg-(--success-S200)'
      : 'bg-(--primary-O300)';

  const creditsLabel = `${course.credits} credits${isTransfer ? ' transferred' : ''}`;

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-(--neutral-black-NB200) px-3 py-2.5">
      <div className="flex min-w-0 items-center gap-3">
        <span
          className={cn(
            'flex size-8 shrink-0 items-center justify-center rounded-md',
            iconWrapClass
          )}
        >
          {isTransfer ? (
            <span className="grid size-3.5 grid-cols-2 gap-0.5">
              <span className="size-1.5 rounded-full bg-(--secondary-B1000)" />
              <span className="size-1.5 rounded-full bg-(--secondary-B1000)" />
              <span className="size-1.5 rounded-full bg-(--secondary-B1000)" />
              <span className="size-1.5 rounded-full bg-(--secondary-B1000)" />
            </span>
          ) : (
            <Star
              className={cn(
                'size-4',
                isCompleted
                  ? 'fill-(--success-S600) text-(--success-S600)'
                  : 'fill-(--primary-O1000) text-(--primary-O1000)'
              )}
            />
          )}
        </span>
        <div className="flex min-w-0 flex-col">
          <span className="flex flex-wrap items-center gap-2 [font-size:var(--fs-b4)]! font-semibold text-(--neutral-black-NB900)!">
            {course.name}
            {isTransfer ? <Badge variant="blue">Transferred</Badge> : null}
            {!isTransfer && badge ? <Badge variant={badge.variant}>{badge.text}</Badge> : null}
            {recommended && !isTransfer ? <Badge variant="default">Recommended</Badge> : null}
            {course.sharedWith === 'associate' ? (
              <Badge variant="purple">Applied to Associate</Badge>
            ) : null}
          </span>
          <span className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB600)!">
            {creditsLabel}
          </span>
        </div>
      </div>

      {hasActions ? (
        <span className="flex shrink-0 items-center gap-3">
          {canSwitch ? (
            <button
              type="button"
              onClick={() => onSwitch?.(course)}
              aria-label="Switch course"
              className="flex shrink-0 cursor-pointer [font-size:var(--fs-l5)]! font-semibold text-(--primary-O1100)! hover:text-(--primary-O900)!"
            >
              Switch
            </button>
          ) : null}
          {canRemove ? (
            <button
              type="button"
              onClick={() => onRemove?.(course)}
              aria-label="Remove course"
              className="flex size-9 shrink-0 cursor-pointer items-center justify-center text-(--neutral-black-NB600) hover:text-(--danger-D700) md:size-5"
            >
              <X className="size-3.5" />
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
};
